import { Button, Text } from "@solomon-labs/design/components/ui";
import React from "react";

import { captureError } from "../../analytics/sentry";

interface ErrorBoundaryState {
    hasError: boolean;
    error?: Error;
    errorInfo?: React.ErrorInfo;
}

interface ErrorBoundaryProps {
    children: React.ReactNode;
    fallback?: React.ComponentType<{ error?: Error; retry: () => void }>;
    onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorFallbackProps {
    error?: Error;
    retry: () => void;
}

function DefaultErrorFallback({
    error,
    retry
}: ErrorFallbackProps): React.ReactElement {
    return (
        <div className="border border-line-base rounded-3xl bg-surface-ground animate-fade-in-content">
            <div className="flex flex-col items-center py-6 px-4 text-center">
                <Text
                    variant="h3"
                    render={<h3 />}
                    className="font-bold text-foreground-base"
                >
                    Something went wrong
                </Text>
                <Text variant="small" className="mt-1 text-foreground-neutral">
                    {error?.message || "An unexpected error occurred"}
                </Text>
                <Button size="sm" onClick={retry} className="mt-4">
                    Try again
                </Button>
            </div>
        </div>
    );
}

class ErrorBoundary extends React.Component<
    ErrorBoundaryProps,
    ErrorBoundaryState
> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return {
            hasError: true,
            error
        };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        this.setState({
            error,
            errorInfo
        });

        this.props.onError?.(error, errorInfo);

        captureError(error, {
            componentStack: errorInfo.componentStack
        });

        console.error("ErrorBoundary caught an error:", error, errorInfo);
    }

    retry = (): void => {
        this.setState({
            hasError: false,
            error: undefined,
            errorInfo: undefined
        });
    };

    render(): React.ReactNode {
        if (this.state.hasError) {
            const FallbackComponent =
                this.props.fallback || DefaultErrorFallback;
            return (
                <FallbackComponent
                    error={this.state.error}
                    retry={this.retry}
                />
            );
        }

        return this.props.children;
    }
}

export { ErrorBoundary };
export type { ErrorBoundaryProps, ErrorBoundaryState };
